
// --- Vue & Template imports ---
import { Component, Vue} from 'vue-property-decorator';
import DownloadExcel from 'vue-json-excel/JsonExcel.vue';

// --- Models ---
import { FilterOption, Status } from '../models/App.model';
import { Load, LoadEvent, LoadStop } from '../models/Load.model';

// --- Services ---
import AppService from '@/services/app';
import CustomerService from '@/services/customer';
import LoadEventService from '@/services/loadEvent';
import LoadService from '@/services/load';
import ReportService from '@/services/report';
import VehicleService from '@/services/vehicle';

// --- Third Party ---
import dayjs from 'dayjs';
import Hashids from 'hashids';

@Component({
  components: { 
    DownloadExcel,
  },
})
export default class SearchLoads extends Vue {

  hashids = new Hashids(process.env.VUE_APP_HASHIDS_SALT);

  // used by Filter Area
  filterOptions: FilterOption[] = [
    {name: 'Status', icon: this.$store.getters['app/icons'].searchStatus },
    {name: 'Client', icon: this.$store.getters['app/icons'].searchCustomers }, 
    {name: 'Load Number', icon: this.$store.getters['app/icons'].searchLoadNumber}, 
    {name: 'Truck', icon: this.$store.getters['app/icons'].searchTruck}, 
    {name: 'Invoice Number', icon: this.$store.getters['app/icons'].searchInvoiceNumber },
    {name: 'Date', icon: this.$store.getters['app/icons'].searchDate },
  ];
  
  searchTypeFilter: FilterOption = { name: '', icon: ''};

  searchCustomerId: number = 0;
  displayStatuses: Status[] = []; // temp solution to hide unused statuses
  selectedStatusIds: number[] = [];
  searchVehicleId: number = 0;
  searchValue: string = '';
  startDatePicker: boolean = false;
  startDate: string = '';
  endDatePicker: boolean = false;
  endDate: string = '';

  searchClicked: boolean = false;

  searchResults: Load[] = [];
  clickedItem: Load = Object.assign({}, this.$store.getters['loads/emptyLoad']);
  expandDelay: boolean = false;
  loadStops: LoadStop[] = [];

  rules = {
    required: (value: string) => !!value || '',
    numbersOnly: (value: string) => (/^\d+$/.test(value)) || 'Invalid character',
    mustHaveOne: (value: Status[]) => value && value.length > 0 || '',
    email: (value: string) => /(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/.test(value) || 'E-mail must be valid',
  };

  // used by Load Event Dialog
  showLoadEventDialog: boolean = false;
  loadEventDialogTitle: string = '';
  loadEvents: LoadEvent[] = [];
  commentFormValid: boolean = false;
  newComment: string = '';
  newCommentIsCritical: boolean = false;
  addCommentClicked: boolean = false;
  criticalClicked: boolean = false;

  // used by Dialog for Resend Proof of Delivery
  showEmailPodDialog: boolean = false;
  emailPodDialogTitle: string = '';
  emailValid: boolean = false;
  toEmailAddress: string = '';
  emailPodClicked: boolean = false;

  // used by Confirm Action Dialog for Download
  action: string = '';
  dialogContent: string = '';
  showConfirmActionDialog: boolean = false;
  confirmActionDelay: boolean = false;

  // #region Functions to display list data on page load
    headers() {
      return [
        {
          text: 'Status',
          value: 'statusId',
        },
        {
          text: 'Client',
          value: 'customerCode',
        },
        {
          text: 'Vehicle',
          value: 'vehicleCodes',
        },
        {
          text: '#',
          value: 'id',
        },
        {
          sortable: false,
          text: 'Order Num',
          value: 'orderNumber',
        },
        {
          text: 'Date',
          value: 'loadDate',
        },
        {
          text: 'From',
          value: 'from',
        },
        {
          text: 'To',
          value: 'to',
        },
        {
          sortable: false,
          text: '',
          value: 'action',
          align: 'end',
        },
      ];
    }

    offloadHeaders() {
      return [
        {
          text: 'Client',
          value: 'client',
        },
        {
          text: 'Address',
          value: 'address',
        },
        {
          text: 'Appointment Time',
          value: 'appointmentTime',
        },
        {
          text: 'Arrived',
          value: 'arrivedAt',
        },
      ];
    }

    customSort(items: any[], sortBy: string[], sortDesc: boolean[], locale: string) {
      // If the result is negative, a is sorted before b.
      // If the result is positive, b is sorted before a.
      // If the result is 0, no changes are done with the sort order of the two values.

      switch (sortBy[0]) {
        case 'statusId':
          // statusId is a number
          if (sortDesc[0]) {
            items.sort((a: Load, b: Load) => a.statusId - b.statusId);
          } else {
            items.sort((a: Load, b: Load) => b.statusId - a.statusId);
          }
          break;

        case 'vehicleCodes':
          if (sortDesc[0]) {
            items.sort((a: Load, b: Load) => (b.horseVehicleCode ? b.horseVehicleCode : 'ZZZ999').toLowerCase().localeCompare((a.horseVehicleCode ? a.horseVehicleCode : 'ZZZ999').toLowerCase()));
          } else {
            items.sort((a: Load, b: Load) => (a.horseVehicleCode ? a.horseVehicleCode : 'ZZZ999').toLowerCase().localeCompare((b.horseVehicleCode ? b.horseVehicleCode : 'ZZZ999').toLowerCase()));
          }
          break;

        case 'customerCode':
          if (sortDesc[0]) {
            items.sort((a: Load, b: Load) => b.customerCode.toLowerCase().localeCompare(a.customerCode.toLowerCase()));
          } else {
            items.sort((a: Load, b: Load) => a.customerCode.toLowerCase().localeCompare(b.customerCode.toLowerCase()));
          }
          break;

        case 'loadDate':
          if (sortDesc[0]) {
            items.sort((a: Load, b: Load) => (a.loadDate ? a.loadDate : '').toLowerCase().localeCompare((b.loadDate ? b.loadDate : '').toLowerCase()));
          } else {
            items.sort((a: Load, b: Load) => (b.loadDate ? b.loadDate : '').toLowerCase().localeCompare((a.loadDate ? a.loadDate : '').toLowerCase()));
          }
          break;

        case 'from':
          if (sortDesc[0]) {
            items.sort((a: Load, b: Load) => b.from.localeCompare(a.from));
          } else {
            items.sort((a: Load, b: Load) => a.from.localeCompare(b.from));
          }
          break;

        case 'to':
          if (sortDesc[0]) {
            items.sort((a: Load, b: Load) => b.to.localeCompare(a.to));
          } else {
            items.sort((a: Load, b: Load) => a.to.localeCompare(b.to));
          }
          break;
      }

      return items;
    }

    async beforeMount() {
      await AppService.listStatuses(false);
      await CustomerService.listAll(false);      
      await VehicleService.listActive(false);
      await VehicleService.listTypes(false);

      this.displayStatuses = this.$store.getters['app/statuses'].filter((item: Status) => item.statusCode !== 'PRELOAD' && item.statusCode !== 'ACCIDENT' && item.statusCode !== 'DELETED' )
    }

    getOneWeekFuture() {
      return dayjs().add(1, 'week').format('YYYY-MM-DD');
    }

    canSearch() {
      switch (this.searchTypeFilter.name) {
        case 'Status':
          return this.selectedStatusIds.length > 0;

        case 'Client':
          return this.searchCustomerId > 0;

        case 'Load Number':
          return this.searchValue;

        case 'Truck':
          return this.searchVehicleId > 0;

        case 'Invoice Number':
          return this.searchValue;

        case 'Date':
          return this.startDate || this.endDate;
      }
    }

    toggleStatus(statusId: number) {
      this.$nextTick(() => {
        if (this.selectedStatusIds.includes(statusId)) {
          // remove this one
          this.selectedStatusIds.splice(this.selectedStatusIds.indexOf(statusId), 1);
        } else {
          //add this one
          this.selectedStatusIds.push(statusId);
        }
      })
    }

    getSelectionIcon(statusId: number) {
      if (this.selectedStatusIds.includes(statusId)) {
        // show selected tickbox
        return 'mdi-checkbox-marked';
      } else {
        // show empty tickbox
        return 'mdi-checkbox-blank-outline';
      }
    }

    resetSearch() {
      this.searchResults = [];
      
      this.searchCustomerId = 0;
      this.selectedStatusIds = [];
      this.searchVehicleId = 0;
      this.searchValue = '';
      this.startDatePicker = false;
      this.startDate = '';
      this.endDatePicker = false;
      this.endDate = '';

      if (this.searchTypeFilter.name === 'Client' || this.searchTypeFilter.name === 'Truck' || this.searchTypeFilter.name === 'Date') {
        this.startDate = dayjs().startOf('month').format('YYYY-MM-DD');
        this.endDate = dayjs().format('YYYY-MM-DD');
      }

      if (this.$refs.searchForm) {
        (this.$refs.searchForm as any).resetValidation();
      }
    }

    async search() {
      this.searchClicked = true;
      try {

        const response = await LoadService.search({ params: {
          customerId: this.searchCustomerId,
          statusIds: (typeof this.selectedStatusIds === 'number' ? this.selectedStatusIds : this.selectedStatusIds.join(',')),
          vehicleId: this.searchVehicleId,
          loadNumber: (this.searchTypeFilter.name === 'Load Number' ? this.searchValue : ''),
          invoiceNumber: (this.searchTypeFilter.name === 'Invoice Number' ? this.searchValue : ''),
          startDate: this.startDate,
          endDate: this.endDate,
        }});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.searchResults = [];
            if (response.data.message === 'Too Much Data Found') {
              // show download popup
              this.launchForcedDownloadResultsDialog();
            } else {
              AppService.errorHandler(response.data.message);
            }
            
          } else {
            this.searchResults = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Search_Loads.vue - search - 1384 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.searchClicked = false;
      }
    }

    async listOffloads(load: Load) {
      try {
        const response = await LoadService.listOffloadsByLoad({ params: { loadId: load.id }});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
            this.loadStops = [];
          } else {
            this.loadStops = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Search_Loads.vue - listOffloads - 1414 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      }
    }

    async expandLoad(item: Load, expand: any) {
      this.loadStops = [];

      if (item.offloadCount && item.offloadCount > 0) {
        // go fetch the load stops for this load
        try {
          this.expandDelay = true;
          await this.listOffloads(item);
        } finally {
          this.expandDelay = false;
        }
      }

      expand(true);
    }

    closeRow(expand: any) {
      expand(false);
    }

    getFormattedLoadDate(loadDate: string) {
      return dayjs(loadDate).format('ddd DD MMM YYYY');
    }

    viewLoadDetails(item: Load) {
      const routeData = this.$router.resolve({name: 'View Load', params: {id: this.hashids.encode(item.id)}});
      window.open(routeData.href, '_blank');
    }

    allowAddCommentFunctionality(item: Load) {
      return item.statusId !== this.$store.getters['app/statusHelper'].deleted && this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},        
      ]);
    }

    allowMakeCommentCriticalFunctionality(item: Load) {
      return item.statusId !== this.$store.getters['app/statusHelper'].deleted && this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
      ]);
    }
  // #endregion

  // #region Functions required for the CSV Download 
    getDataForCSV() {
      return this.searchResults.reduce((a: any, b: any) => {
        let newItem = b;
        newItem = Object.assign({}, b, {  });

        return a.concat(newItem);
      }, []);
    }

    getFieldsForCSV() {
      // NOTE: If you edit this, you need to edit the Forced Download CSV being generated in go handler-load.go : DownloadLoadsFromSearch
      return {
        'Load Date': 'loadDate',
        'Load Number': 'id',
        'Client': 'customerCode',        
        'Truck': 'horseVehicleCode',
        'Start Odo': 'startKm',
        'End Odo': 'endKm',
        'OLD Truck': 'oldHorseVehicleCode',
        'OLD Truck - Start Odo': 'oldHorseStartKm',
        'OLD Truck - End Odo': 'oldHorseEndKm',
        'Driver': 'driverNickname',
        'Comments': 'criticalComment',
        'Rate': 'rate',
        'From': 'from',
        'To': 'to',
        'Trailer 1': 'trailer1VehicleCode',
        'Trailer 2': 'trailer2VehicleCode',
        'Order No': 'orderNumber',
        'Operator': 'operatorName',
        'Loading Time': 'loadingEventTime',
        'Final Stop Offloading Time': 'offloadingEventTime',
        'Status': 'statusName',
        'Invoice No': 'invoiceNumber',
        'Invoice Date': 'invoiceDate',
        
      };
    }
  // #endregion

  // #region Functions required for the List Load Events / Add Comment Dialog    
    async listLoadEvents(loadId: number) {
      try {
        // always check for all the comments in case someone else added one in the background - until we are using websockets
        const response = await LoadEventService.listByLoad({params: {loadId: loadId}});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.loadEvents = [];
          } else {
           this.loadEvents = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Search_Loads.vue - listLoadEvents - 1513 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      }
    }

    async launchLoadEventDialog(item: Load) {
      this.clickedItem = Object.assign({}, item);
      this.newComment = '';
      this.newCommentIsCritical = false;
      
      // fetch load events associated with this load
      await this.listLoadEvents(this.clickedItem.id);

      this.commentFormValid = false;
      this.loadEventDialogTitle = 'Events for Load #' + this.clickedItem.id;

      if (this.$refs.commentForm) {
        (this.$refs.commentForm as any).resetValidation();
      }

      this.showLoadEventDialog = true;
    }

    async markAsCritical(item: LoadEvent, isCritical: boolean) {
      try {
        this.criticalClicked = true;

        const response = await LoadEventService.markAsCritical({
          loadEventId: item.id,
          isCritical: isCritical,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            await this.search();
            await this.listLoadEvents(this.clickedItem.id);
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Search_Loads.vue - markAsCritical - 1563 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.criticalClicked = false;
      }
    }

    async createComment() {
      try {
        this.addCommentClicked = true;

        const response = await LoadEventService.createComment({
          loadId: this.clickedItem.id,
          comment: this.newComment,
          isCritical: this.newCommentIsCritical,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.search();
            this.showLoadEventDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Search_Loads.vue - createComment - 1599 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.addCommentClicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Dialog to Resend Proof of Delivery
    async launchEmailPodDialog(item: Load) {
      this.clickedItem = item;

      this.emailValid = false;
      this.toEmailAddress = '';
      this.emailPodDialogTitle = 'Resend Proof of Delivery for Load #' + this.clickedItem.id;

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.showEmailPodDialog = true;
    }

    async resendProofOfDelivery() {
      try {
        this.emailPodClicked = true;

        const response = await ReportService.resendProofOfDelivery({
          customerId: this.clickedItem.customerId,
          loadId: this.clickedItem.id,
          title: 'Proof of Delivery - Load #' + this.clickedItem.id,
          emailAddress: this.toEmailAddress,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            this.showEmailPodDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Search_Loads.vue - resendProofOfDelivery - 1650 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.emailPodClicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Confirm Action Dialog for Download
    launchForcedDownloadResultsDialog() {
      this.action = 'FORCE_DOWNLOAD';
      this.dialogContent = '<span style="font-weight: 700;color: #C01212">Your search returned too many results.</span><br/><br/>'
        + 'Would you like to rather download the results?';

      this.showConfirmActionDialog = true;
    }

    ConfirmActionDialog_cancel() {
      this.showConfirmActionDialog = false;
    }

    async ConfirmActionDialog_doConfirmed() {
      if (this.action === 'FORCE_DOWNLOAD') {
        await this.doForcedDownload();
      }
    }

    async doForcedDownload() {
      try {
        this.confirmActionDelay = true;
        const response = await LoadService.forcedDownload({ params: {
          customerId: this.searchCustomerId,
          statusIds: (typeof this.selectedStatusIds === 'number' ? this.selectedStatusIds : this.selectedStatusIds.join(',')),
          vehicleId: this.searchVehicleId,
          loadNumber: (this.searchTypeFilter.name === 'Load Number' ? this.searchValue : ''),
          invoiceNumber: (this.searchTypeFilter.name === 'Invoice Number' ? this.searchValue : ''),
          startDate: this.startDate,
          endDate: this.endDate,
        }});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            // fake link approach: https://stackoverflow.com/questions/58292771/downloading-a-csv-of-file-using-vue-and-js
            const anchor = document.createElement('a');
            anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(response.data);
            anchor.target = '_blank';
            anchor.download = 'search_results.csv';
            anchor.click();

            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Search_Loads.vue - doForcedDownload - 1786 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;        
      }
    }
  // #endregion
}
